import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import Appbar from '../../components/Appbar';
import { useAppVersionsStore } from '../../services/appVersions';
import type { AppVersionSettings } from '../../services/appVersions';

const versionPattern = /^\d+(?:\.\d+)*$/;

const compareVersions = (left: string, right: string) => {
  const leftParts = left.split('.').map(Number);
  const rightParts = right.split('.').map(Number);
  const length = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < length; index += 1) {
    const leftPart = leftParts[index] || 0;
    const rightPart = rightParts[index] || 0;

    if (leftPart !== rightPart) {
      return leftPart - rightPart;
    }
  }

  return 0;
};

type FieldErrors = Record<string, string>;

const defaultForm: AppVersionSettings = {
  android: {
    softUpdateVersion: '',
    hardUpdateVersion: '',
    storeUrl: '',
  },
  ios: {
    softUpdateVersion: '',
    hardUpdateVersion: '',
    storeUrl: '',
  },
};

const AppVersions = () => {
  const { data, isLoading, isSubmitting, fetchSettings, saveSettings } = useAppVersionsStore();
  const [form, setForm] = useState<AppVersionSettings>(defaultForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    setForm(data);
  }, [data]);

  const sections = useMemo(
    () => [
      { key: 'android' as const, title: 'Android', storeLabel: 'Play Store URL' },
      { key: 'ios' as const, title: 'iOS', storeLabel: 'App Store URL' },
    ],
    [],
  );

  const setPlatformField = (
    platform: 'android' | 'ios',
    field: 'softUpdateVersion' | 'hardUpdateVersion' | 'storeUrl',
    value: string,
  ) => {
    setForm((current) => ({
      ...current,
      [platform]: {
        ...current[platform],
        [field]: value,
      },
    }));

    setFieldErrors((current) => {
      const next = { ...current };
      delete next[`${platform}.${field}`];
      return next;
    });
  };

  const validate = () => {
    const nextErrors: FieldErrors = {};

    sections.forEach(({ key }) => {
      const config = form[key];

      if (!config.softUpdateVersion.trim()) {
        nextErrors[`${key}.softUpdateVersion`] = 'Soft update version is required';
      } else if (!versionPattern.test(config.softUpdateVersion.trim())) {
        nextErrors[`${key}.softUpdateVersion`] = 'Use a version like 1.0.13';
      }

      if (!config.hardUpdateVersion.trim()) {
        nextErrors[`${key}.hardUpdateVersion`] = 'Hard update version is required';
      } else if (!versionPattern.test(config.hardUpdateVersion.trim())) {
        nextErrors[`${key}.hardUpdateVersion`] = 'Use a version like 1.0.13';
      }

      if (
        !nextErrors[`${key}.softUpdateVersion`] &&
        !nextErrors[`${key}.hardUpdateVersion`] &&
        compareVersions(config.hardUpdateVersion.trim(), config.softUpdateVersion.trim()) > 0
      ) {
        nextErrors[`${key}.hardUpdateVersion`] =
          'Hard update version cannot be greater than soft update version';
      }

      if (!config.storeUrl.trim()) {
        nextErrors[`${key}.storeUrl`] = 'Store URL is required';
      } else {
        try {
          const parsed = new URL(config.storeUrl.trim());
          if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            nextErrors[`${key}.storeUrl`] = 'Store URL must start with http or https';
          }
        } catch (_err) {
          nextErrors[`${key}.storeUrl`] = 'Store URL must be a valid URL';
        }
      }
    });

    return nextErrors;
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Appbar appBarTitle="App Versions" />
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Container sx={{ mt: 4, mb: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="h5" gutterBottom>
                  App Version Settings
                </Typography>
                <Typography color="text.secondary">
                  Configure soft update, hard update, and store links for Android and iOS.
                </Typography>
              </Box>
              <Button variant="outlined" onClick={fetchSettings} disabled={isLoading || isSubmitting}>
                Refresh
              </Button>
            </Stack>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={3}>
                {sections.map((section) => (
                  <Paper key={section.key} variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {section.title}
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        label="Soft Update Version"
                        value={form[section.key].softUpdateVersion}
                        onChange={(event) =>
                          setPlatformField(section.key, 'softUpdateVersion', event.target.value)
                        }
                        error={Boolean(fieldErrors[`${section.key}.softUpdateVersion`])}
                        helperText={fieldErrors[`${section.key}.softUpdateVersion`] || ''}
                        fullWidth
                      />
                      <TextField
                        label="Hard Update Version"
                        value={form[section.key].hardUpdateVersion}
                        onChange={(event) =>
                          setPlatformField(section.key, 'hardUpdateVersion', event.target.value)
                        }
                        error={Boolean(fieldErrors[`${section.key}.hardUpdateVersion`])}
                        helperText={fieldErrors[`${section.key}.hardUpdateVersion`] || ''}
                        fullWidth
                      />
                      <TextField
                        label={section.storeLabel}
                        value={form[section.key].storeUrl}
                        onChange={(event) =>
                          setPlatformField(section.key, 'storeUrl', event.target.value)
                        }
                        error={Boolean(fieldErrors[`${section.key}.storeUrl`])}
                        helperText={fieldErrors[`${section.key}.storeUrl`] || ''}
                        fullWidth
                      />
                    </Stack>
                  </Paper>
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={async () => {
                      const errors = validate();
                      setFieldErrors(errors);
                      if (Object.keys(errors).length > 0) {
                        return;
                      }

                      await saveSettings({
                        android: {
                          softUpdateVersion: form.android.softUpdateVersion.trim(),
                          hardUpdateVersion: form.android.hardUpdateVersion.trim(),
                          storeUrl: form.android.storeUrl.trim(),
                        },
                        ios: {
                          softUpdateVersion: form.ios.softUpdateVersion.trim(),
                          hardUpdateVersion: form.ios.hardUpdateVersion.trim(),
                          storeUrl: form.ios.storeUrl.trim(),
                        },
                      });
                    }}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Stack>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default AppVersions;
